/**
 * Number formatting for quantities (NFR-36).
 *
 * The API sends a `Decimal` as a string at full scale — `"1.500"`, `"150.000"` — because
 * that is the only representation that survives JSON without becoming a float (NFR-13).
 * Nobody wants to read `1.500 kg` on a recipe, so the rounding happens here, at the last
 * moment, and never in the stored value.
 */

/** Display: max two decimals, trailing zeros dropped, French comma. `"1.500"` → `1,5`. */
const display = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 })

/**
 * The editor's formatter. Same rounding, but no thousands separator: `Intl` puts a narrow
 * no-break space in `1 500`, and there is no key on any keyboard that types it back — the
 * field would round-trip a value its own validation then rejects.
 */
const editable = new Intl.NumberFormat('fr-FR', {
  maximumFractionDigits: 2,
  useGrouping: false,
})

function parse(value: string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null
  // The comma is accepted on the way in as well as written on the way out — the backend's
  // FrenchDecimalField takes it, so a value read straight back out of an input can land here.
  const parsed = Number(String(value).replace(',', '.'))
  return Number.isFinite(parsed) ? parsed : null
}

/** For reading: `"1.500"` → `"1,5"`, `"150.000"` → `"150"`. Unparseable input passes through. */
export function formatQuantity(value: string | null | undefined): string {
  const parsed = parse(value)
  return parsed === null ? '' : display.format(parsed)
}

/** For typing into: as above, without the grouping separator. */
export function quantityToInput(value: string | null | undefined): string {
  const parsed = parse(value)
  return parsed === null ? '' : editable.format(parsed)
}

/**
 * A comment's timestamp: `"2026-08-12T14:05:00Z"` → `12 août 2026, 14:05`.
 *
 * Absolute rather than "il y a 2 jours": relative time needs a bucketing helper and a timer
 * to stay true as the page sits open, and it is vaguest exactly where a thread needs to be
 * precise — two notes a day apart on the same recipe.
 */
const dateTime = new Intl.DateTimeFormat('fr-FR', {
  dateStyle: 'long',
  timeStyle: 'short',
})

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return ''
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? '' : dateTime.format(parsed)
}

/** The subset of a unit this module needs — `/api/meta/units/` supplies the rest. */
interface UnitOption {
  value: string
  label: string
  is_implicit: boolean
}

/**
 * The measurement half of an ingredient line: `"1,5 kg"`, `"3"`, `"une pincée de"` → `""`.
 *
 * Units flagged implicit print as the bare quantity (NFR-38) — "3 oignons", not "3 pièces
 * oignons", because counting is what you mean when you don't name a unit. Which units those
 * are comes from the payload, never from a `piece` literal here: the vocabulary has one
 * source and it is `recipes/units.py` (invariant 3).
 */
export function formatMeasure(
  quantity: string | null | undefined,
  unit: string | null | undefined,
  units: UnitOption[] | undefined,
): string {
  const amount = formatQuantity(quantity)
  const match = unit ? units?.find((candidate) => candidate.value === unit) : undefined

  if (!unit || match?.is_implicit) return amount
  // An unknown unit still prints: better a raw code than silently dropping information.
  return [amount, match?.label ?? unit].filter(Boolean).join(' ')
}
